<script setup lang="ts">
import { BaseButton, BaseInput, BasePassword } from '@components/base';
import { AUTH_ROUTES } from '@constants/auth-routes';
import { getPostLoginRedirectUrl } from '@helpers/user-menu';
import { UserLoginPayload } from '@models/user';
import { useUserStore } from '@stores/index';
import { useVuelidate } from '@vuelidate/core';
import { email, required, minLength } from '@vuelidate/validators';
import { reactive, computed } from 'vue';

const props = withDefaults(
  defineProps<{
    content?: Record<string, string>;
  }>(),
  { content: () => ({}) },
);

const userStore = useUserStore();
const formData = reactive<UserLoginPayload>({
  loginName: '',
  password: '',
});

const rules = {
  loginName: { email, required },
  password: { required, minValue: minLength(6) },
};

const v$ = useVuelidate(rules, formData);

const emit = defineEmits<{ 'open-forgot-password': [] }>();

const title = computed(() => props.content?.title ?? 'Welcome back');
const forgotPasswordText = computed(() => props.content?.forgotPassword ?? 'Forgot password?');
const createAccountText = computed(() => props.content?.createAccount ?? 'Create an account');
const loginNameError = computed(() => v$.value.loginName.$errors[0]?.$message ?? '');
const passwordError = computed(() => v$.value.password.$errors[0]?.$message ?? '');

const onSubmit = async () => {
  if (userStore.loading) return;

  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const result = await userStore.login(formData);
  if (result && userStore.isUserTOTPResponse(result)) {
    // TOTP flow - could add modal or inline step
    return;
  }
  if (result) {
    window.location.assign(getPostLoginRedirectUrl(userStore.info));
  }
};
</script>

<template>
  <form
    class="flex w-full flex-col gap-24"
    @submit.prevent="onSubmit"
  >
    <h1 class="font-inter-tight text-20 font-semibold text-off-black-500 lg:text-26">
      {{ title }}
    </h1>

    <div class="flex flex-col gap-16">
      <BaseInput
        :model-value="formData.loginName"
        type="email"
        placeholder="Email"
        autocomplete="email"
        :error="v$.loginName.$error"
        :error-message="(loginNameError as unknown) as string"
        @update:model-value="formData.loginName = $event.trim()"
      />

      <BasePassword
        v-model="formData.password"
        placeholder="Password"
        autocomplete="current-password"
        :error="v$.password.$error"
        :error-message="(passwordError as unknown) as string"
      />

      <div class="flex justify-end">
        <button
          type="button"
          class="text-14 font-medium text-teal-500 hover:underline"
          @click="emit('open-forgot-password')"
        >
          {{ forgotPasswordText }}
        </button>
      </div>
    </div>

    <BaseButton
      text="LOGIN"
      type="submit"
      variant="primary"
      class="!flex !h-[44px] !min-h-[44px] !w-full items-center justify-center !rounded-full !py-0 !text-16 !font-medium !uppercase"
      :loading="userStore.loading"
      :disabled="v$.$error"
    >
      <template #icon-right>
        <span
          class="size-20 opacity-0"
          aria-hidden="true"
        />
      </template>
    </BaseButton>

    <p class="text-center text-14 text-off-black-500">
      First time here?
      <a
        :href="AUTH_ROUTES.SIGN_UP"
        class="font-medium text-teal-500 hover:underline"
      >
        {{ createAccountText }}
      </a>
    </p>
  </form>
</template>
